import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Track } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTrackFilter } from "@/hooks/use-track-filter";
import { Search, Loader2, Music, Layers as LayersIcon, Filter as FilterIcon, Play, Pause, Clock, HeartPulse, Plus } from "lucide-react";
import { Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AudioPlayer } from "@/components/ui/audio-player";

const useCases = [
  { value: "all", label: "All Tracks" },
  { value: "social_media", label: "Social Media" },
  { value: "tv", label: "TV & Film" },
  { value: "corporate", label: "Corporate" },
  { value: "podcast", label: "Podcasts" }
];

const genres = [
  { value: "all", label: "All Genres" },
  { value: "Electronic", label: "Electronic" },
  { value: "Hip Hop", label: "Hip Hop" },
  { value: "Pop", label: "Pop" },
  { value: "Ambient", label: "Ambient" },
  { value: "Corporate", label: "Corporate" }
];

const moods = [
  { value: "all", label: "All Moods" },
  { value: "Upbeat", label: "Upbeat" },
  { value: "Relaxed", label: "Relaxed" },
  { value: "Energetic", label: "Energetic" },
  { value: "Inspirational", label: "Inspirational" },
  { value: "Mysterious", label: "Mysterious" }
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { 
      staggerChildren: 0.05,
      when: "beforeChildren" 
    } 
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { 
    y: 0, 
    opacity: 1,
    transition: { type: "spring", stiffness: 300, damping: 24 }
  }
};

export default function Catalog() {
  const [selectedTracks, setSelectedTracks] = useState<Track[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [allLoadedTracks, setAllLoadedTracks] = useState<Track[]>([]);
  const [showAllTracks, setShowAllTracks] = useState(false);
  const { toast } = useToast();
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  
  // Audio playback state
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState<number | null>(null);
  const [playingStates, setPlayingStates] = useState<Record<number, boolean>>({});
  
  // Fetch tracks with pagination - load all available tracks when showAllTracks is true
  const { data, isLoading, isFetching, error, refetch } = useQuery<{ 
    tracks: Track[],
    pagination: {
      total: number,
      page: number,
      limit: number,
      totalPages: number
    }
  }>({ 
    queryKey: ['/api/tracks', { page: currentPage, limit: 1000 }], // Always try to get 1000 tracks
    // Force refetch when showAllTracks changes to ensure we get all tracks
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0, // Always consider data stale
  });

  // Add state for sorting
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    if (error) {
      console.error("Failed to load tracks:", error);
      toast({
        title: "Error Loading Tracks",
        description: "There was a problem loading tracks. Please try again.",
        variant: "destructive",
      });
    }
  }, [error, toast]);

  const allTracks = data?.tracks || [];
  const pagination = data?.pagination || { total: 0, page: 1, limit: 100, totalPages: 1 };

  const {
    filterTracks,
    filters,
    setUseCase,
    setGenre,
    setMood,
    setSearchQuery,
    searchQuery
  } = useTrackFilter(allLoadedTracks);
  
  // Apply sorting to filtered tracks
  const filteredTracks = React.useMemo(() => {
    const tracks = filterTracks();
    return [...tracks].sort((a, b) => {
      if (sortOrder === 'asc') {
        return a.title.localeCompare(b.title);
      } else {
        return b.title.localeCompare(a.title);
      }
    });
  }, [filterTracks, sortOrder]);
  
  // Toggle sort order
  const toggleSortOrder = () => {
    setSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc');
  };
  
  // Force load ALL tracks on first render
  useEffect(() => {
    console.log('INITIAL COMPONENT MOUNT - LOADING ALL TRACKS');
    
    // Reset all state
    setAllLoadedTracks([]);
    setCurrentPage(1);
    
    // Direct fetch all tracks with maximum limit
    fetch('/api/tracks?limit=1000')
      .then(response => response.json())
      .then(result => {
        console.log('DIRECT FETCH RESULT:', result);
        if (result.tracks && result.tracks.length > 0) {
          console.log(`Successfully loaded ${result.tracks.length} tracks via direct fetch`);
          setAllLoadedTracks(result.tracks);
          
          // Show toast
          toast({
            title: "Tracks Loaded",
            description: `Loaded ${result.tracks.length} of ${result.pagination.total} tracks.`,
          });
        }
      })
      .catch(err => {
        console.error('Error fetching all tracks:', err);
        toast({
          title: "Error Loading Tracks",
          description: "Failed to load tracks. Please try refreshing the page.",
          variant: "destructive",
        });
      });
  }, []); // Empty dependency ensures this only runs once
  
  // Handle load more button click - explicitly handle pagination
  const handleLoadMore = () => {
    if (currentPage < pagination.totalPages) {
      // Force pagination mode if we're using load more
      setShowAllTracks(false);
      setCurrentPage(prev => prev + 1);
      
      toast({
        title: "Loading More Tracks",
        description: `Loading page ${currentPage + 1} of ${pagination.totalPages}...`,
      });
    }
  };
  
  // Handle Show All Tracks button click - CRITICAL for loading ALL tracks at once
  const handleShowAllTracks = () => {
    // Clear any existing loaded tracks first
    setAllLoadedTracks([]);
    // Set to show all tracks mode and reset to page 1
    setShowAllTracks(true);
    setCurrentPage(1);
    
    // Show a toast to notify user that all tracks are being loaded
    toast({
      title: "Loading All Tracks",
      description: `Loading all ${pagination.total} tracks. This may take a moment...`,
    });
  };
  
  const handleAddToLicense = (track: Track) => {
    if (!selectedTracks.some(t => t.id === track.id)) {
      setSelectedTracks([...selectedTracks, track]);
    }
  };
  
  const handleRemoveTrack = (trackId: number) => {
    setSelectedTracks(selectedTracks.filter(track => track.id !== trackId));
  };

  const handlePlayClick = (trackId: number) => {
    // Toggle the playing state for this track
    setPlayingStates(prev => {
      // Create a copy of the current state
      const newStates = { ...prev };
      // If this track is currently playing, stop it
      if (newStates[trackId]) {
        newStates[trackId] = false;
      } else {
        // Otherwise, stop all tracks and play this one
        Object.keys(newStates).forEach(key => {
          newStates[parseInt(key)] = false;
        });
        newStates[trackId] = true;
      }
      return newStates;
    });
  };

  // Handle track ended
  const handleTrackEnded = (trackId: number) => {
    setPlayingStates(prev => ({ ...prev, [trackId]: false }));
  };

  return (
    <div className="min-h-screen pb-20 pt-8 bg-background">
      <div className="container mx-auto px-4">
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-3xl font-bold text-foreground">Music Catalog</h1>
            <div className="flex items-center space-x-2">
              <Link href="/mood-editor">
                <Button variant="outline" className="border-primary text-primary hover:bg-primary/10">
                  <HeartPulse className="mr-2 h-4 w-4" /> 
                  Mood Editor
                </Button>
              </Link>
              <Badge variant="outline" className="text-primary border-primary py-1 px-3">
                <Music className="w-3.5 h-3.5 mr-1.5" />
                {pagination.total} Total Tracks
              </Badge>
              
              <Badge variant="outline" className="text-primary border-primary py-1 px-3">
                <LayersIcon className="w-3.5 h-3.5 mr-1.5" />
                {allLoadedTracks.length} Loaded
              </Badge>
            </div>
          </div>
          
          <p className="text-muted-foreground mb-6">
            Browse through our extensive collection of original tracks. Filter by genre, mood, or use case 
            to find the perfect soundtrack for your creative project.
          </p>
          
          {/* Filter buttons */}
          <div className="flex flex-wrap gap-2 mb-6">
            {useCases.map(useCase => (
              <Button
                key={useCase.value}
                variant={filters.useCase === useCase.value || (filters.useCase === null && useCase.value === 'all') ? "default" : "outline"}
                onClick={() => setUseCase(useCase.value === 'all' ? null : useCase.value)}
                className={filters.useCase === useCase.value || (filters.useCase === null && useCase.value === 'all') 
                  ? "bg-primary text-primary-foreground hover:bg-primary/90" 
                  : "border-primary text-primary hover:bg-primary/10"}
              >
                {useCase.label}
              </Button>
            ))}
          </div>
          
          {/* Control bar with search and sort */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                type="text"
                placeholder="Search tracks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border-border focus:border-primary"
              />
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Select
                  value={filters.genre || "all"}
                  onValueChange={(value) => setGenre(value === "all" ? null : value)}
                >
                  <SelectTrigger className="bg-background text-foreground border-border w-36">
                    <SelectValue placeholder="All Genres" />
                  </SelectTrigger>
                  <SelectContent>
                    {genres.map((genre) => (
                      <SelectItem key={genre.value} value={genre.value}>
                        {genre.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select
                  value={filters.mood || "all"}
                  onValueChange={(value) => setMood(value === "all" ? null : value)}
                >
                  <SelectTrigger className="bg-background text-foreground border-border w-36">
                    <SelectValue placeholder="All Moods" />
                  </SelectTrigger>
                  <SelectContent>
                    {moods.map((mood) => (
                      <SelectItem key={mood.value} value={mood.value}>
                        {mood.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button 
                  variant="outline" 
                  onClick={toggleSortOrder}
                  className="border-border text-foreground"
                >
                  Sort {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Table header - visible on larger screens only */}
        <div className="hidden md:grid grid-cols-12 gap-4 border-b border-border pb-2 mb-4 text-sm text-muted-foreground">
          <div className="col-span-5 flex items-center">TITLE</div>
          <div className="col-span-2 flex items-center">MOOD</div>
          <div className="col-span-2 flex items-center"><HeartPulse className="w-4 h-4 mr-1" /> BPM</div>
          <div className="col-span-2 flex items-center"><Clock className="w-4 h-4 mr-1" /> LENGTH</div>
          <div className="col-span-1 flex items-center justify-end">ACTION</div>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <motion.div 
              className="w-12 h-12"
              animate={{ 
                rotate: 360,
                borderRadius: ["20%", "50%", "20%"]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                ease: "linear" 
              }}
            >
              <div className="w-full h-full bg-gradient-to-br from-primary/80 to-primary/40 rounded-lg"></div>
            </motion.div>
            <p className="mt-4 text-muted-foreground">Loading your tracks...</p>
          </div>
        ) : error ? (
          <motion.div 
            className="text-center p-10 rounded-lg bg-destructive/10 border border-destructive/20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-destructive mb-4">Failed to load tracks</p>
            <Button 
              onClick={() => refetch()}
              variant="destructive"
            >
              Retry
            </Button>
          </motion.div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-1"
          >
            {filteredTracks.length === 0 ? (
              <motion.div 
                className="text-center py-12 text-muted-foreground"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                No tracks match your search criteria
              </motion.div>
            ) : (
              <>
                {filteredTracks.map((track) => (
                  <motion.div
                    key={track.id}
                    variants={itemVariants}
                    className={`group grid grid-cols-12 gap-4 items-center p-3 rounded-lg transition-colors duration-200 
                      ${hoveredId === track.id ? 'bg-primary/5' : 'hover:bg-primary/5'} 
                      ${playingStates[track.id] ? 'bg-primary/10 border-l-4 border-primary' : 'border-l-4 border-transparent'}`}
                    onMouseEnter={() => setHoveredId(track.id)}
                    onMouseLeave={() => setHoveredId(null)}
                  >
                    {/* Title and play button */}
                    <div className="col-span-12 md:col-span-5 flex items-center gap-3">
                      <button
                        onClick={() => handlePlayClick(track.id)}
                        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors
                          ${playingStates[track.id]
                            ? 'bg-primary text-primary-foreground' 
                            : 'bg-background/50 border border-border text-foreground group-hover:bg-primary group-hover:text-primary-foreground'}`}
                      >
                        {playingStates[track.id] ? (
                          <Pause className="w-4 h-4" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </button>
                      
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-foreground truncate">{track.title}</h3>
                        <p className="text-xs text-muted-foreground md:hidden mt-1">
                          <span className="font-medium text-primary">{track.bpm ? `${track.bpm} BPM` : 'No BPM'}</span> • {track.duration || '3:24'}
                        </p>
                        
                        {/* If this track is playing, show the audio player */}
                        <AnimatePresence>
                          {playingStates[track.id] && (
                            <motion.div 
                              className="mt-2 w-full"
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <AudioPlayer 
                                src={track.filePath}
                                trackId={track.id}
                                isPlaying={true}
                                onPlayPause={() => handlePlayClick(track.id)}
                                onEnded={() => handleTrackEnded(track.id)}
                                mini={true}
                                className="w-full"
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                    
                    {/* Mood tag - hidden on mobile */}
                    <div className="hidden md:flex col-span-2 items-center">
                      <span className="px-2 py-1 bg-primary/10 rounded-full text-xs text-primary">
                        {track.mood || "Energetic"}
                      </span>
                    </div>
                    
                    {/* BPM - hidden on mobile */}
                    <div className="hidden md:flex col-span-2 items-center text-foreground">
                      <span className="font-medium">
                        {track.bpm ? `${track.bpm} BPM` : "-"}
                      </span>
                    </div>
                    
                    {/* Duration - hidden on mobile */}
                    <div className="hidden md:flex col-span-2 items-center text-foreground">
                      {track.duration || "3:24"}
                    </div>
                    
                    {/* Action buttons - hidden on mobile */}
                    <div className="hidden md:flex col-span-1 items-center justify-end">
                      <button 
                        onClick={() => handleAddToLicense(track)}
                        className="w-8 h-8 rounded-full bg-transparent hover:bg-primary/10 flex items-center justify-center text-primary hover:text-primary transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                ))}
                
                {/* Selected Tracks Panel */}
                {selectedTracks.length > 0 && (
                  <div className="fixed bottom-20 right-4 z-30 bg-card text-foreground border border-border rounded-lg shadow-lg p-4 max-w-md w-full">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-bold text-foreground">Selected Tracks ({selectedTracks.length})</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedTracks([])}
                        className="text-foreground hover:text-primary"
                      >
                        Clear
                      </Button>
                    </div>
                    <div className="max-h-40 overflow-y-auto mb-3">
                      {selectedTracks.map((track) => (
                        <div key={track.id} className="flex justify-between items-center py-1">
                          <span className="text-sm text-foreground">{track.title}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveTrack(track.id)}
                            className="text-foreground hover:text-primary"
                          >
                            &times;
                          </Button>
                        </div>
                      ))}
                    </div>
                    <Link href="/license">
                      <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                        Proceed to License ({selectedTracks.length})
                      </Button>
                    </Link>
                  </div>
                )}
                
                {/* Pagination controls */}
                <motion.div 
                  className="flex justify-between items-center mt-8 pt-4 border-t border-border text-sm"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1, transition: { delay: 0.5 } }}
                >
                  <span className="text-muted-foreground">
                    Showing {filteredTracks.length} of {pagination.total} tracks
                  </span>
                  
                  <div className="flex space-x-2">
                    {currentPage < pagination.totalPages && (
                      <Button 
                        variant="outline"
                        className="border-primary text-primary hover:bg-primary/10"
                        onClick={handleLoadMore}
                        disabled={isFetching}
                      >
                        {isFetching ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Loading
                          </>
                        ) : (
                          <>Load More</>
                        )}
                      </Button>
                    )}
                    
                    {!showAllTracks && pagination.total > allLoadedTracks.length && (
                      <Button 
                        variant="default"
                        className="bg-primary text-primary-foreground hover:bg-primary/90"
                        onClick={handleShowAllTracks}
                        disabled={isFetching}
                      >
                        Show All Tracks
                      </Button>
                    )}
                  </div>
                </motion.div>
              </>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
